import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LOGO_IMG } from "../../data";
import { useLang } from "../../i18n";
import { ArrowDownRight } from "lucide-react";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.1, delay: 0.5 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = ({ onNavigate }) => {
  const { t } = useLang();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  const lines = [h.l1, "l2", h.l3, h.l4];

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden pt-20">
      <motion.div style={{ y: imgY }} className="absolute top-[34%] right-0 md:right-0 w-[42%] md:w-[52%] max-w-[720px] z-10">
        <img src={LOGO_IMG} alt="Lilah C. Photography" className="w-full h-auto object-contain floaty" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-20 px-6 md:px-12 pt-[14vh] md:pt-[18vh]">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }} className="overline text-[#E2725B] mb-6 md:mb-10">
          {h.overline}
        </motion.p>

        <h1 className="font-serif-display font-light leading-[0.86] tracking-tight text-[15vw] md:text-[11vw] lg:text-[10rem] text-[#2D2A26]">
          {lines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span custom={i} variants={lineReveal} initial="hidden" animate="show" className={`block ${i === 3 ? "italic" : ""}`}>
                {line === "l2" ? <>{h.l2a} {h.l2b && <span className="italic text-[#E2725B]">{h.l2b}</span>}</> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.9, duration: 0.9 }} className="mt-10 md:mt-16 flex flex-col md:flex-row md:items-end gap-8 md:gap-24 max-w-4xl">
          <p className="text-[#2D2A26]/70 text-base md:text-lg max-w-sm leading-relaxed">{h.desc}</p>
          <button data-testid="hero-cta" onClick={() => onNavigate("book")} className="group inline-flex items-center gap-3 shrink-0">
            <span className="flex items-center justify-center w-16 h-16 rounded-full border border-[#2D2A26]/30 group-hover:bg-[#E2725B] group-hover:border-[#E2725B] transition-all duration-400">
              <ArrowDownRight className="w-6 h-6 group-hover:text-[#F9F6F0] transition-colors" strokeWidth={1.2} />
            </span>
            <span className="overline text-[0.66rem]">{h.cta}</span>
          </button>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.4, duration: 1 }} className="absolute bottom-8 left-6 md:left-12 overline text-[0.55rem] text-[#2D2A26]/50">
        {h.scroll}
      </motion.div>
    </section>
  );
};
